import { Button, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { CIMSModule, pathify } from '@cims-online/shared'
import { useAuthContext } from 'context/AuthContext'
import { useRouteMatches } from 'hooks/useRouteMatches'
import { cimsStorage } from 'utils/storage'
import { REACT_APP_LOGIN_URL } from 'config'

/**
 * this page is rendered when you first come to the site, regardless of what the
 * url is due to the ProtectedRoute component's auth guard in its render prop
 *
 * the following use effect should handle all edge cases we care about for that
 * initial render
 *
 * most of the time, you'll be redirected to where you should go, unless the server
 * isn't up, in which case you can use the button to trigger the flow again
 *
 * there is some interplay between a couple components you should be aware of
 * 1. the Routes component should handle and redirect deprecated paths to their
 *    current ones, which will likely send users here if they aren't authenticated
 * 2. the SideNav component should handle manually entered urls while in the
 *    application context and navigation by page selection
 * 3. the ProtectedRoute component should handle disallowed navigation and help
 *    unauthenticated users get here, making use of a `from` state variable in the
 *    router state so we can send them back to where they wanted to go in the first
 *    place
 */
const Login: React.FC = () => {
  /** see ProtectedRoute for why we're using `from` here */
  const { state } = useLocation<{ from?: string }>()
  const { user } = useAuthContext()
  const { push } = useHistory()
  const [from, setFrom] = useState<string>()
  const { param } = useRouteMatches(from)
  useEffect(() => {
    if (state?.from) setFrom(state.from)
  }, [state])

  useEffect(() => {
    // if there's no user, we still need to log in and will keep coming here until we're done
    if (!user) return
    return push(cimsStorage.path ?? pathify(CIMSModule.Home.pathname))
  }, [user, push, param])

  return (
    <>
      {!user && (
        <Typography.Paragraph>
          Please log in to use this application
        </Typography.Paragraph>
      )}
      {user && (
        <Typography.Paragraph>
          For security reasons, user sessions expire over time. Please log in
          again to refresh it.
        </Typography.Paragraph>
      )}
      <Button type="primary" href={REACT_APP_LOGIN_URL}>
        Log In
      </Button>
    </>
  )
}

export default Login
